import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogFooter,
  DialogType,
  PrimaryButton,
  DefaultButton,
  Checkbox,
  Dropdown,
  IDropdownOption,
  Stack,
  TextField,
  Label,
} from "@fluentui/react";
import { useSelector } from "../../store/hooks";

type BulkUpdateField = {
  key: string;
  name: string;
  taxonomyKey?: string;
  include?: string[];
  enabled: boolean;
  value: string | number | boolean | null;
  type?: "taxonomy" | "weighting" | "boolean" | "text";
};

type Props = {
  isOpen: boolean;
  onDismiss: () => void;
  onApply: (updates: Record<string, any>) => void;
  selectedCount: number;
};

export const BulkUpdateDialog = ({
  isOpen,
  onDismiss,
  onApply,
  selectedCount,
}: Props) => {
  const { t } = useTranslation();
  const taxonomy = useSelector((s) => s.taxonomy);
  const [comment, setComment] = useState<string>("");
  const [commentError, setCommentError] = useState<string>("");
  const [hideFromInsurer, setHideFromInsurer] = useState<boolean>(true);

  const getInitialFields = (): BulkUpdateField[] => [
    {
      key: "improvementPotentialCode",
      name: t("ram.recommendations.column.improvementPotentialCode.label"),
      taxonomyKey: "RiskImprovementPotential",
      type: "taxonomy",
      enabled: false,
      value: null,
    },
    {
      key: "statusCompanyCode",
      name: t("ram.recommendations.column.statusCompanyCode.label"),
      taxonomyKey: "RiskImplementationStatus",
      type: "taxonomy",
      enabled: false,
      value: null,
    },
    {
      key: "typeCode",
      name: t("ram.recommendations.column.typeCode.label"),
      taxonomyKey: "RiskCategoryOfMeasure",
      type: "taxonomy",
      enabled: false,
      value: null,
    },
    {
      key: "subCategory1Code",
      name: t("ram.recommendations.column.subCategory1Code.label"),
      taxonomyKey: "RiskAssessmentSubcategory",
      type: "taxonomy",
      enabled: false,
      value: null,
    },
    {
      key: "weighting",
      name: t("ram.recommendations.column.weighting.label"),
      type: "weighting",
      enabled: false,
      value: null,
    },
    {
      key: "priorityCompanyCode",
      name: t("ram.recommendations.column.priorityCompanyCode.label"),
      taxonomyKey: "RiskPriority",
      include: [
        "RiskPriority.MINOR",
        "RiskPriority.MODERATE",
        "RiskPriority.MAJOR",
      ],
      type: "taxonomy",
      enabled: false,
      value: null,
    },
    {
      key: "priorityCode",
      name: t("ram.recommendations.column.priorityCode.label"),
      taxonomyKey: "RiskPriority",
      include: [
        "RiskPriority.MINOR",
        "RiskPriority.MODERATE",
        "RiskPriority.MAJOR",
      ],
      type: "taxonomy",
      enabled: false,
      value: null,
    },
    {
      key: "improvementPotentialCodeAsIf",
      name: t("ram.recommendationForm.improvementPotentialCodeAsIf.label"),
      taxonomyKey: "RiskImprovementPotential",
      type: "taxonomy",
      enabled: false,
      value: null,
    },
    {
      key: "costEstimateCode",
      name: t("ram.recommendationForm.costEstimateCode.label"),
      taxonomyKey: "RiskReductionCost",
      type: "taxonomy",
      enabled: false,
      value: null,
    },
  ];

  const [fields, setFields] = useState<BulkUpdateField[]>(getInitialFields());

  // Reset fields when dialog opens
  useEffect(() => {
    if (isOpen) {
      setFields(getInitialFields());
      setComment("");
      setCommentError("");
      setHideFromInsurer(true);
    }
  }, [isOpen]);

  const getTaxonomyOptions = (
    taxonomyKey: string,
    include?: string[]
  ): IDropdownOption[] => {
    const taxonomyData = (taxonomy as any)[taxonomyKey];
    if (!taxonomyData?.items) return [];

    let items = taxonomyData.items;
    if (include) {
      items = items.filter((item: any) => include.includes(item.code));
    }

    return items.map((item: any) => ({
      key: item.id,
      text: t(item.code) || item.code || item.id,
    }));
  };

  const handleCheckboxChange = (key: string, checked: boolean) => {
    setFields((prev) =>
      prev.map((field) =>
        field.key === key
          ? { ...field, enabled: checked, value: checked ? field.value : null }
          : field
      )
    );
  };

  const handleBooleanValueChange = (key: string, checked: boolean) => {
    setFields((prev) =>
      prev.map((field) =>
        field.key === key ? { ...field, value: checked } : field
      )
    );
  };

  const handleValueChange = (key: string, value: any) => {
    setFields((prev) =>
      prev.map((field) => (field.key === key ? { ...field, value } : field))
    );
  };

  const handleApply = () => {
    // Validate comment is provided
    if (!comment.trim()) {
      setCommentError(
        t("ram.recommendationsEditable.bulkUpdate.commentRequired")
      );
      return;
    }

    // Validate that all enabled taxonomy/weighting fields have values
    const invalidFields = fields.filter(
      (f) =>
        f.enabled &&
        f.type !== "boolean" &&
        (f.value === null || f.value === undefined)
    );
    if (invalidFields.length > 0) {
      alert(t("ram.recommendationsEditable.bulkUpdate.validationError"));
      return;
    }

    const updates: Record<string, any> = {
      comment: comment.trim(),
      hideFromInsurer: hideFromInsurer,
    };
    fields.forEach((field) => {
      if (field.enabled && field.value !== null) {
        updates[field.key] = field.value;
      }
    });
    onApply(updates);
    onDismiss();
  };

  const renderField = (field: BulkUpdateField) => {
    if (field.type === "boolean") {
      return (
        <Stack
          key={field.key}
          horizontal
          tokens={{ childrenGap: 10 }}
          verticalAlign="center"
        >
          <Checkbox
            checked={field.enabled}
            onChange={(_, checked) =>
              handleCheckboxChange(field.key, checked || false)
            }
            styles={{ root: { width: 30 } }}
          />
          <Stack.Item grow={1}>
            <Stack
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: 10 }}
            >
              <Label>{field.name}</Label>
              <Checkbox
                checked={field.value as boolean}
                onChange={(_, checked) =>
                  handleBooleanValueChange(field.key, checked || false)
                }
                disabled={!field.enabled}
              />
            </Stack>
          </Stack.Item>
        </Stack>
      );
    }

    if (field.type === "taxonomy" && field.taxonomyKey) {
      return (
        <Stack
          key={field.key}
          horizontal
          tokens={{ childrenGap: 10 }}
          verticalAlign="center"
        >
          <Checkbox
            checked={field.enabled}
            onChange={(_, checked) =>
              handleCheckboxChange(field.key, checked || false)
            }
            styles={{ root: { width: 30 } }}
          />
          <Stack.Item grow={1}>
            <Dropdown
              label={field.name}
              options={getTaxonomyOptions(field.taxonomyKey, field.include)}
              selectedKey={field.value as string | number | null}
              onChange={(_, option) =>
                handleValueChange(field.key, option?.key)
              }
              disabled={!field.enabled}
              placeholder={t(
                "ram.recommendationsEditable.bulkUpdate.selectValue"
              )}
            />
          </Stack.Item>
        </Stack>
      );
    }

    // weighting type - numeric dropdown 1-5
    return (
      <Stack
        key={field.key}
        horizontal
        tokens={{ childrenGap: 10 }}
        verticalAlign="center"
      >
        <Checkbox
          checked={field.enabled}
          onChange={(_, checked) =>
            handleCheckboxChange(field.key, checked || false)
          }
          styles={{ root: { width: 30 } }}
        />
        <Stack.Item grow={1}>
          <Dropdown
            label={field.name}
            options={[
              { key: 1, text: "1" },
              { key: 2, text: "2" },
              { key: 3, text: "3" },
              { key: 4, text: "4" },
              { key: 5, text: "5" },
              { key: 6, text: "6" },
              { key: 7, text: "7" },
              { key: 8, text: "8" },
              { key: 9, text: "9" },
              { key: 10, text: "10" },
            ]}
            selectedKey={field.value as number | null}
            onChange={(_, option) => handleValueChange(field.key, option?.key)}
            disabled={!field.enabled}
            placeholder={t(
              "ram.recommendationsEditable.bulkUpdate.selectValue"
            )}
          />
        </Stack.Item>
      </Stack>
    );
  };

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={onDismiss}
      dialogContentProps={{
        type: DialogType.normal,
        title: t("ram.recommendationsEditable.bulkUpdate.title"),
        subText: t("ram.recommendationsEditable.bulkUpdate.description"),
      }}
      modalProps={{
        isBlocking: false,
        styles: {
          main: {
            minWidth: "1000px !important",
            maxWidth: "1200px !important",
            width: "1100px !important",
          },
        },
      }}
    >
      <Stack tokens={{ childrenGap: 15 }}>
        <Stack horizontal tokens={{ childrenGap: 20 }}>
          <Stack
            tokens={{ childrenGap: 15 }}
            styles={{ root: { width: "50%" } }}
          >
            {fields.slice(0, Math.ceil(fields.length / 2)).map(renderField)}
          </Stack>
          <Stack
            tokens={{ childrenGap: 15 }}
            styles={{ root: { width: "50%" } }}
          >
            {fields.slice(Math.ceil(fields.length / 2)).map(renderField)}
          </Stack>
        </Stack>
        <TextField
          label={t("ram.recommendations.column.comment.label")}
          value={comment}
          onChange={(_, newValue) => {
            setComment(newValue || "");
            if (newValue?.trim()) {
              setCommentError("");
            }
          }}
          multiline
          rows={4}
          required
          errorMessage={commentError}
          placeholder={t("ram.recommendations.column.comment.label")}
        />
        <Checkbox
          label={t("ram.recommendations.column.hideFromInsurer.label")}
          checked={hideFromInsurer}
          onChange={(_, checked) => setHideFromInsurer(checked || false)}
        />
      </Stack>
      <DialogFooter>
        <PrimaryButton onClick={handleApply} text={t("greco.apply")} />
        <DefaultButton onClick={onDismiss} text={t("greco.cancel")} />
      </DialogFooter>
    </Dialog>
  );
};
